import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import FooterGoogleReview from '../Footer/FooterGoogleReview'

const PhoneSync = dynamic(() => import('./PhoneSync'), { ssr: false })

declare global {
	interface Window {
		$wc_leads?: any
	}
}

interface FooterNonAnimatedData {
	logo?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	// Address
	address_heading?: {
		text?: string
		title?: string
	}
	address_text?: {
		text?: string
		title?: string
	}
	// Phone
	phone_heading?: {
		text?: string
		title?: string
	}
	phone_text?: {
		text?: string
		title?: string
	}
	// Fax
	fax_heading?: {
		text?: string
		title?: string
	}
	fax_text?: {
		text?: string
		title?: string
	}
	// Email
	email_heading?: {
		text?: string
		title?: string
	}
	email_text?: {
		text?: string
		title?: string
	}
	// Navigation heading links
	about_us_heading_link?: {
		href: string
		label: string
	}
	rhinoplasty_heading_link?: {
		href: string
		label: string
	}
	facial_surgery_heading_link?: {
		href: string
		label: string
	}
	med_spa_heading_link?: {
		href: string
		label: string
	}
	// Link collections
	about_us_links?: {
		menu_items_id: {
			href: string
			label: string
		}
	}[]
	rhinoplasty_links?: {
		menu_items_id: {
			href: string
			label: string
		}
	}[]
	facial_surgery_links?: {
		menu_items_id: {
			href: string
			label: string
		}
	}[]
	med_spa_links?: {
		menu_items_id: {
			href: string
			label: string
		}
	}[]
	// Social links
	social_links?: {
		external_links_id: {
			url: string
			aria_label: string
			target: string
			icon?: {
				id: string
				filename_download: string
			}
		}
	}[]
	// Bottom links
	bottom_links?: {
		menu_items_id: {
			href: string
			label: string
		}
	}[]
	// Copyright
	copyright_text?: {
		text?: string
		title?: string
	}
	// Google review
	google_review_link?: {
		url: string
		aria_label: string
		target: string
	}
	google_review_rating?: number | string
	google_review_count?: number
}

export const fields = [
	// Logo
	'logo.alt',
	'logo.image_file.image.id',
	'logo.image_file.image.filename_download',

	// Address
	'address_heading.title',
	'address_heading.text',
	'address_text.title',
	'address_text.text',

	// Phone
	'phone_heading.title',
	'phone_heading.text',
	'phone_text.title',
	'phone_text.text',

	// Fax
	'fax_heading.title',
	'fax_heading.text',
	'fax_text.title',
	'fax_text.text',

	// Email
	'email_heading.title',
	'email_heading.text',
	'email_text.title',
	'email_text.text',

	// Navigation heading links
	'about_us_heading_link.href',
	'about_us_heading_link.label',
	'rhinoplasty_heading_link.href',
	'rhinoplasty_heading_link.label',
	'facial_surgery_heading_link.href',
	'facial_surgery_heading_link.label',
	'med_spa_heading_link.href',
	'med_spa_heading_link.label',

	// Link collections
	'about_us_links.menu_items_id.href',
	'about_us_links.menu_items_id.label',

	'rhinoplasty_links.menu_items_id.href',
	'rhinoplasty_links.menu_items_id.label',

	'facial_surgery_links.menu_items_id.href',
	'facial_surgery_links.menu_items_id.label',

	'med_spa_links.menu_items_id.href',
	'med_spa_links.menu_items_id.label',

	// Social links
	'social_links.external_links_id.url',
	'social_links.external_links_id.aria_label',
	'social_links.external_links_id.target',
	'social_links.external_links_id.icon.id',
	'social_links.external_links_id.icon.filename_download',

	// Bottom links
	'bottom_links.menu_items_id.href',
	'bottom_links.menu_items_id.label',

	// Copyright
	'copyright_text.title',
	'copyright_text.text',

	// Google review
	'google_review_link.url',
	'google_review_link.aria_label',
	'google_review_link.target',
	'google_review_rating',
	'google_review_count',
] as const

interface FooterNonAnimatedProps {
	data?: FooterNonAnimatedData
}

export default function FooterNonAnimated({ data }: FooterNonAnimatedProps) {
	return (
		<div className='overflow-hidden bg-primary w-full flex items-center py-8 lg:py-32'>
			<footer className='w-full bg-primary max-h-screen overflow-y-auto  pb-12 xl:pb-0'>
				<div className='gl-container'>
					{/* Logo Section */}
					<div className='mb-12 xl:mb-12 flex justify-center xl:justify-start'>
						<div className='w-[280px] h-[69px] xl:w-[280px] xl:h-[69px] relative'>
							<Image
								src={
									data?.logo
										? getReadableImageUrl(
												data.logo.image_file.image.id,
												data.logo.image_file.image.filename_download
										  )
										: '/images/white-bader-logo.svg'
								}
								alt={data?.logo?.alt || ''}
								fill
								className='object-contain'
								priority
								quality={90}
							/>
						</div>
					</div>

					{/* Contact and Navigation Grid */}
					<div className='xl:flex xl:justify-between gap-8 xl:gap-12'>
						{/* Address Column */}
						<div className='space-y-2.5 text-center xl:text-left'>
							<p className='text-white text-base font-semibold font-inter uppercase'>
								{data?.address_heading?.title ||
									data?.address_heading?.text ||
									''}
							</p>
							<Link
								href='https://g.co/kgs/TYEBX9G'
								target='_blank'
								rel='noopener noreferrer'
								className='text-white text-[1rem] font-normal leading-normal font-greycliff tracking-tight [&>p]:mb-2'
							>
								<div
									dangerouslySetInnerHTML={sanitizeHTML(
										data?.address_text?.text || data?.address_text?.title || ''
									)}
								/>
							</Link>
						</div>

						{/* Contact Info Column */}
						<div className='mt-8 xl:mt-0 space-y-5 text-center xl:text-left'>
							<div className='space-y-2.5'>
								<p className='text-white text-base font-semibold font-inter uppercase'>
									{data?.phone_heading?.title ||
										data?.phone_heading?.text ||
										''}
								</p>
								<p className='text-white text-[1rem] font-normal leading-normal font-greycliff tracking-tight'>
									<PhoneSync 
										phoneNumber={data?.phone_text?.text || data?.phone_text?.title || ''}
									/>
								</p>
							</div>

							<div className='space-y-0.5'>
								<p className='text-white text-base font-semibold font-inter uppercase'>
									{data?.fax_heading?.title || data?.fax_heading?.text || ''}
								</p>
								<p className='text-white text-[1rem] font-normal leading-normal font-greycliff tracking-tight'>
									{data?.fax_text?.text || data?.fax_text?.title || ''}
								</p>
							</div>

							<div className='space-y-1.5'>
								<p className='text-white text-base font-semibold font-inter uppercase'>
									{data?.email_heading?.title ||
										data?.email_heading?.text ||
										''}
								</p>
								<p className='text-white text-[1rem] font-normal leading-normal font-greycliff tracking-tight'>
									<Link
										href={`mailto:${
											data?.email_text?.text || data?.email_text?.title || ''
										}`}
										className='text-white hover:text-gray-200'
									>
										{data?.email_text?.text || data?.email_text?.title || ''}
									</Link>
								</p>
							</div>
						</div>

						{/* About Column */}
						<div className='mt-8 xl:mt-0 space-y-3 text-center xl:text-left'>
							<p className='text-white text-base font-semibold font-inter uppercase'>
								{data?.about_us_heading_link ? (
									<Link
										href={data.about_us_heading_link.href || '#'}
										className='hover:text-gray-300 transition-colors'
									>
										{data.about_us_heading_link.label || ''}
									</Link>
								) : (
									''
								)}
							</p>
							<div className='flex flex-col'>
								{(data?.about_us_links || []).map((link, index) => (
									<Link
										key={index}
										href={link.menu_items_id.href || '#'}
										className='text-white text-[1rem] font-light font-inter uppercase leading-normal hover:text-gray-300 transition-colors'
									>
										{link.menu_items_id.label || ''}
									</Link>
								))}
							</div>
						</div>

						{/* Rhinoplasty Column */}
						<div className='mt-8 xl:mt-0 space-y-3 text-center xl:text-left'>
							<p className='text-white text-base font-semibold font-inter uppercase'>
								{data?.rhinoplasty_heading_link ? (
									<Link
										href={data.rhinoplasty_heading_link.href || '#'}
										className='hover:text-gray-300 transition-colors'
									>
										{data.rhinoplasty_heading_link.label || ''}
									</Link>
								) : (
									''
								)}
							</p>
							<div className='flex flex-col'>
								{(data?.rhinoplasty_links || []).map((link, index) => (
									<Link
										key={index}
										href={link.menu_items_id.href || '#'}
										className='text-white text-[1rem] font-light font-inter uppercase leading-normal hover:text-gray-300 transition-colors'
									>
										{link.menu_items_id.label || ''}
									</Link>
								))}
							</div>
						</div>

						{/* Facial Plastic Surgery Column */}
						<div className='mt-6 xl:mt-0 space-y-3 text-center xl:text-left'>
							<p className='text-white text-base font-semibold font-inter uppercase'>
								{data?.facial_surgery_heading_link ? (
									<Link
										href={data.facial_surgery_heading_link.href || '#'}
										className='hover:text-gray-300 transition-colors'
									>
										{data.facial_surgery_heading_link.label || ''}
									</Link>
								) : (
									''
								)}
							</p>
							<div className='flex flex-col'>
								{(data?.facial_surgery_links || []).map((link, index) => (
									<Link
										key={index}
										href={link.menu_items_id.href || '#'}
										className='text-white text-[1rem] font-light font-inter uppercase leading-normal hover:text-gray-300 transition-colors'
									>
										{link.menu_items_id.label || ''}
									</Link>
								))}
							</div>
						</div>

						{/* Med Spa Services Column */}
						<div className='mt-6 xl:mt-0 space-y-3 text-center xl:text-left'>
							<p className='text-white text-base font-semibold font-inter uppercase'>
								{data?.med_spa_heading_link ? (
									<Link
										href={data.med_spa_heading_link.href || '#'}
										className='hover:text-gray-300 transition-colors'
									>
										{data.med_spa_heading_link.label || ''}
									</Link>
								) : (
									''
								)}
							</p>
							<div className='flex flex-col'>
								{(data?.med_spa_links || []).map((link, index) => (
									<Link
										key={index}
										href={link.menu_items_id.href || '#'}
										className='text-white text-[1rem] font-light font-inter uppercase leading-normal hover:text-gray-300 transition-colors'
									>
										{link.menu_items_id.label || ''}
									</Link>
								))}
							</div>
						</div>

						{/* Instagram Icon Column */}
						<div className='mt-6 xl:mt-0 flex justify-center xl:justify-start w-auto xl:w-10'>
							{(data?.social_links || []).map((link, index) => (
								<a
									key={index}
									href={link.external_links_id.url || ''}
									className='text-white'
									aria-label={link.external_links_id.aria_label || ''}
									target={link.external_links_id.target || '_self'}
								>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										width='18'
										height='19'
										viewBox='0 0 18 19'
										fill='none'
									>
										<path
											fillRule='evenodd'
											clipRule='evenodd'
											d='M13 0.5H5C2.23858 0.5 0 2.73858 0 5.5V13.5C0 16.2614 2.23858 18.5 5 18.5H13C15.7614 18.5 18 16.2614 18 13.5V5.5C18 2.73858 15.7614 0.5 13 0.5ZM16.25 13.5C16.2445 15.2926 14.7926 16.7445 13 16.75H5C3.20735 16.7445 1.75549 15.2926 1.75 13.5V5.5C1.75549 3.70735 3.20735 2.25549 5 2.25H13C14.7926 2.25549 16.2445 3.70735 16.25 5.5V13.5ZM13.75 5.75C14.3023 5.75 14.75 5.30228 14.75 4.75C14.75 4.19772 14.3023 3.75 13.75 3.75C13.1977 3.75 12.75 4.19772 12.75 4.75C12.75 5.30228 13.1977 5.75 13.75 5.75ZM9 5C6.51472 5 4.5 7.01472 4.5 9.5C4.5 11.9853 6.51472 14 9 14C11.4853 14 13.5 11.9853 13.5 9.5C13.5027 8.3057 13.0294 7.15957 12.1849 6.31508C11.3404 5.47059 10.1943 4.99734 9 5ZM6.25 9.5C6.25 11.0188 7.4812 12.25 9 12.25C10.5188 12.25 11.75 11.0188 11.75 9.5C11.75 7.9812 10.5188 6.75 9 6.75C7.4812 6.75 6.25 7.9812 6.25 9.5Z'
											fill='currentColor'
										/>
									</svg>
								</a>
							))}
						</div>
					</div>

					{/* FooterNonAnimated Bottom */}
					<div className='mt-8 xl:mt-16 pt-4 border-t border-white'>
						<div className='flex flex-col items-center xl:items-start gap-2.5 xl:flex-row xl:justify-between'>
							<div className='flex flex-col items-center xl:items-start gap-2.5 xl:flex-row xl:gap-20'>
								{(data?.bottom_links || []).map((link, index) => (
									<Link
										key={index}
										href={link.menu_items_id.href || '#'}
										className='text-[#efefef] text-xs font-normal font-inter leading-tight hover:text-white transition-colors'
									>
										{link.menu_items_id.label || ''}
									</Link>
								))}
							</div>
							<div className='flex flex-col items-center xl:items-end gap-2 max-w-[50vw]'>
								<div
									className='text-[#efefef] text-xs font-normal font-inter leading-tight text-center xl:text-right [&>p]:mb-2 [&_a]:underline [&_a]:important [&_a:hover]:no-underline'
									dangerouslySetInnerHTML={sanitizeHTML(
										data?.copyright_text?.text ||
											data?.copyright_text?.title ||
											''
									)}
								/>
								<FooterGoogleReview
									url={data?.google_review_link?.url}
									ariaLabel={data?.google_review_link?.aria_label}
									target={data?.google_review_link?.target}
									rating={data?.google_review_rating}
									reviewCount={data?.google_review_count}
								/>
							</div>
						</div>
					</div>
				</div>
			</footer>
		</div>
	)
}
